import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  listCustomers,
  createCustomer,
  type Customer,
  type CustomerInput,
} from "../api/admin";
import Icon from "../components/icons";

const trLower = (s: string) => s.toLocaleLowerCase("tr-TR");
// Muhasebe ile AYNI sütunlar burada da var; biri ayraçlı biri çıplak gösterirse
// aynı müşterinin bakiyesi iki sayfada farklı okunur (2026-08-07).
const sayi = (n: number) => n.toLocaleString("tr-TR");
// Kayıt anı = organizations.created_at (self-servis kayıtta kullanıcıyla aynı an).
// Biçim diğer admin listeleriyle aynı (Unsubscribes, AnsweredQuestions).
function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

const EMPTY: CustomerInput = {
  name: "",
  type: "kurumsal",
  email: "",
  status: "active",
  login_email: "",
  login_password: "",
};

export default function Customers() {
  const [rows, setRows] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<CustomerInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = trLower(q.trim());
    if (!needle) return rows;
    return rows.filter((c) => trLower(c.name).includes(needle) || trLower(c.email || "").includes(needle));
  }, [rows, q]);

  const loginEmail = (form.login_email ?? "").trim();
  const loginPassword = form.login_password ?? "";
  const canSubmit = !!form.name.trim() && !!loginEmail && loginPassword.length >= 6;

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      setRows(await listCustomers());
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setErr(null);
    setNotice(null);
    try {
      const { user } = await createCustomer(form);
      setForm(EMPTY);
      setShowForm(false);
      setNotice(`Müşteri oluşturuldu. Panel girişi: ${user.email}`);
      await reload();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Oluşturma hatası");
    } finally {
      setSaving(false);
    }
  }

  // NOT: "Pasifleştir" ve "Kredi ekle" bu listeden KALDIRILDI (2026-08-07).
  // Liste tarama içindir; durum değiştirmek ve kredi tanımlamak müşteri
  // bağlamı gerektiren işlemlerdir ve Müşteri Detay sayfasında duruyor.
  // Yetenek kaybı yok — yalnızca yeri değişti.

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>Müşteriler</h1>
          <span className="muted">{q.trim() ? `${filtered.length}/${rows.length}` : rows.length} kayıt</span>
        </div>
        <p className="page-sub">Müşteri firmaları, panel giriş hesapları, durum ve kredi yönetimi.</p>
      </div>

      <div className="toolbar">
        <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          <Icon name={showForm ? "close" : "plus"} size={16} />
          {showForm ? "Kapat" : "Yeni Müşteri"}
        </button>
      </div>

      {showForm && (
      <form className="card customer-form" onSubmit={onCreate}>
        <div className="card-head">
          <h2 className="section-title" style={{ margin: 0 }}>Yeni Müşteri</h2>
        </div>
        <div className="cf-grid">
          <label className="cf-field">
            <span>Ad / Firma adı</span>
            <input
              placeholder="Örn. Makdos Work"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label className="cf-field">
            <span>Tip</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as CustomerInput["type"] })}>
              <option value="kurumsal">Kurumsal</option>
              <option value="bireysel">Bireysel</option>
            </select>
          </label>
          <label className="cf-field">
            <span>Firma e-postası <em>(opsiyonel)</em></span>
            <input
              type="email"
              placeholder="info@firma.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>
          <label className="cf-field">
            <span>Panel giriş e-postası</span>
            <input
              type="email"
              placeholder="kullanici@firma.com"
              value={form.login_email ?? ""}
              onChange={(e) => setForm({ ...form, login_email: e.target.value })}
              autoComplete="off"
              required
            />
          </label>
          <label className="cf-field">
            <span>Panel giriş parolası <em>(min 6)</em></span>
            <input
              type="password"
              placeholder="••••••"
              value={form.login_password ?? ""}
              onChange={(e) => setForm({ ...form, login_password: e.target.value })}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </label>
        </div>
        <div className="cf-actions">
          <button type="submit" disabled={saving || !canSubmit}>
            {saving ? "Ekleniyor…" : "Müşteri Ekle"}
          </button>
        </div>
      </form>
      )}

      {notice && <div className="notice">{notice}</div>}
      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        {/* Arama araç çubuğu kartın İÇİNDE — panel deseni. Liste ve filtresi
            aynı yüzeyde durur, sayfa başlığıyla karışmaz. */}
        <div className="data-table-toolbar">
          <label className="data-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              placeholder="Ad veya e-posta ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Müşterilerde ara"
            />
          </label>
          <span className="data-count">
            {q.trim() ? `${filtered.length}/${rows.length}` : rows.length} kayıt
          </span>
        </div>
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Icon name="inbox" size={34} />
            <p>Henüz müşteri yok.</p>
            <button className="btn-primary" onClick={() => setShowForm(true)}>
              <Icon name="plus" size={16} /> İlk müşteriyi ekle
            </button>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="dt-num">ID</th>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>E-posta</th>
                  <th>Durum</th>
                  <th>Kayıt Tarihi</th>
                  <th className="dt-num">Kredi</th>
                  {/* Kredi "ne kadar hakkı kaldı", bu sütun "ne kadar üretti"
                      der — ikisi farklı sorular. */}
                  <th className="dt-num">Üretilen GSPR</th>
                  <th style={{ textAlign: "right" }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="dt-empty">“{q}” ile eşleşen müşteri yok.</td>
                  </tr>
                ) : filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="dt-num dt-muted">{c.id}</td>
                    <td>
                      <div className="dt-main">
                        <span className="dt-avatar">{(c.name || "?").charAt(0).toLocaleUpperCase("tr-TR")}</span>
                        <span className="dt-name" title={c.name}>{c.name}</span>
                      </div>
                    </td>
                    <td><span className="badge neutral">{c.type === "kurumsal" ? "Kurumsal" : "Bireysel"}</span></td>
                    <td className="dt-muted">{c.email || "—"}</td>
                    <td>
                      <span className={"badge " + (c.status === "active" ? "ok" : "off")}>
                        {c.status === "active" ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    <td className="dt-nowrap dt-muted">{fmtDate(c.created_at)}</td>
                    <td className="dt-num">
                      <span className={"badge " + (c.credit_balance > 0 ? "ok" : "off")}>
                        {sayi(c.credit_balance)}
                      </span>
                    </td>
                    <td className="dt-num">
                      {c.gspr_count > 0 ? sayi(c.gspr_count) : <span className="dt-muted">—</span>}
                    </td>
                    <td>
                      <div className="dt-actions">
                        {/* İkon-only bağlantı: erişilebilir ad zorunlu
                            (design-system §2). */}
                        <Link
                          className="dt-icon-btn"
                          to={`/customers/${c.id}`}
                          title={`${c.name} — detay`}
                          aria-label={`${c.name} müşterisinin detayını aç`}
                        >
                          <Icon name="eye" size={16} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
